// 130 Assessment Questions
// Tags are used for scoring but NEVER shown to users

export interface Question {
  id: string;
  tag: string;
  text: string;
}

export const QUESTIONS: Question[] = [
  // A: Patient equity/advocacy (10 items)
  { id: "A1", tag: "A", text: "I prioritise patient access and continuity, even when it creates pressure." },
  { id: "A2", tag: "A", text: "I routinely ask whose voice is missing when we make practice decisions." },
  { id: "A3", tag: "A", text: "I am comfortable redesigning services to reduce health inequalities." },
  { id: "A4", tag: "A", text: "I prefer decisions that improve patient experience, even if the gains are not easily measurable." },
  { id: "A5", tag: "A", text: "I challenge policies that unintentionally disadvantage groups of patients." },
  { id: "A6", tag: "A", text: "I see safeguarding and vulnerability as everyone's business, not a delegated task." },
  { id: "A7", tag: "A", text: "I will accept short-term inconvenience if it improves long-term patient outcomes." },
  { id: "A8", tag: "A", text: "I value relationship-based care as a core part of general practice." },
  { id: "A9", tag: "A", text: "I focus on what matters to patients, not just what is clinically 'correct'." },
  { id: "A10", tag: "A", text: "I am willing to defend patient-centred practice even when external targets dominate." },

  // B: People leadership (10 items)
  { id: "B1", tag: "B", text: "I give direct feedback quickly rather than letting issues drift." },
  { id: "B2", tag: "B", text: "I actively invest in developing others, even when time is tight." },
  { id: "B3", tag: "B", text: "I notice morale changes early and intervene before it becomes a bigger problem." },
  { id: "B4", tag: "B", text: "I prefer to teach or coach people to a solution rather than solving problems for them." },
  { id: "B5", tag: "B", text: "I set clear expectations and follow through consistently." },
  { id: "B6", tag: "B", text: "I address poor behaviour even when the person is clinically strong." },
  { id: "B7", tag: "B", text: "I pay attention to psychological safety in meetings and day-to-day work." },
  { id: "B8", tag: "B", text: "I try to build a culture where people can disagree respectfully." },
  { id: "B9", tag: "B", text: "I find it energising to recruit, induct, and retain good people." },
  { id: "B10", tag: "B", text: "I am willing to have difficult conversations to protect the team and patients." },

  // C: Operational grip (10 items)
  { id: "C1", tag: "C", text: "I prefer clear processes and standard ways of working." },
  { id: "C2", tag: "C", text: "I regularly check whether we are delivering what we said we would deliver." },
  { id: "C3", tag: "C", text: "I feel uncomfortable when responsibilities are unclear." },
  { id: "C4", tag: "C", text: "I like to turn ideas into simple operational plans with owners and timelines." },
  { id: "C5", tag: "C", text: "I use data and dashboards to stay on top of performance." },
  { id: "C6", tag: "C", text: "I value consistent clinical and admin workflows across the practice." },
  { id: "C7", tag: "C", text: "I focus on reducing variation to improve reliability and safety." },
  { id: "C8", tag: "C", text: "I enjoy solving bottlenecks, capacity problems, and inefficiencies." },
  { id: "C9", tag: "C", text: "I will push back on 'special cases' if they undermine a workable system." },
  { id: "C10", tag: "C", text: "I believe small operational fixes can make a bigger difference than grand strategies." },

  // D: Financial stewardship (10 items)
  { id: "D1", tag: "D", text: "I feel confident making decisions that protect partner drawings in the long term." },
  { id: "D2", tag: "D", text: "I want financial information presented clearly and reviewed regularly." },
  { id: "D3", tag: "D", text: "I question whether we are getting value for money in practice spending." },
  { id: "D4", tag: "D", text: "I am comfortable stopping work that is unfunded or poorly aligned with our strategy." },
  { id: "D5", tag: "D", text: "I prefer decisions that strengthen sustainability, even if they are unpopular initially." },
  { id: "D6", tag: "D", text: "I consider opportunity cost when we take on new services or projects." },
  { id: "D7", tag: "D", text: "I want partners to understand income streams and cost drivers." },
  { id: "D8", tag: "D", text: "I am willing to financially invest in the practice if the business case is strong." },
  { id: "D9", tag: "D", text: "I keep a close eye on staffing costs, productivity, and capacity." },
  { id: "D10", tag: "D", text: "I think entrepreneurial thinking can be appropriate in NHS general practice." },

  // E: Governance/risk (10 items)
  { id: "E1", tag: "E", text: "I take complaints seriously as a chance to improve systems, not blame individuals." },
  { id: "E2", tag: "E", text: "I value clear documentation and evidence of good practice." },
  { id: "E3", tag: "E", text: "I prefer to identify risks early, even if it creates extra work." },
  { id: "E4", tag: "E", text: "I feel confident chairing discussions about clinical risk and safety." },
  { id: "E5", tag: "E", text: "I believe quality improvement should be routine, not occasional." },
  { id: "E6", tag: "E", text: "I am prepared to be firm about compliance and standards." },
  { id: "E7", tag: "E", text: "I keep professional boundaries clear when decision-making could be conflicted." },
  { id: "E8", tag: "E", text: "I want the practice to be inspection-ready at all times." },
  { id: "E9", tag: "E", text: "I actively support learning from significant events and near misses." },
  { id: "E10", tag: "E", text: "I prioritise resilience planning for workforce, IT, and continuity of care." },

  // F: Strategy/innovation (10 items)
  { id: "F1", tag: "F", text: "I enjoy creating direction and helping others see the bigger picture." },
  { id: "F2", tag: "F", text: "I am comfortable piloting new ideas without having every detail fixed first." },
  { id: "F3", tag: "F", text: "I regularly ask what our practice should stop doing to make space for priorities." },
  { id: "F4", tag: "F", text: "I prefer to shape change rather than react to it." },
  { id: "F5", tag: "F", text: "I seek evidence, but I do not let perfection block progress." },
  { id: "F6", tag: "F", text: "I enjoy turning external pressures into opportunities for redesign." },
  { id: "F7", tag: "F", text: "I think in terms of 12 to 36 months, not just the next week." },
  { id: "F8", tag: "F", text: "I am willing to challenge 'how we have always done it'." },
  { id: "F9", tag: "F", text: "I like testing different models, then scaling what works." },
  { id: "F10", tag: "F", text: "I find it motivating to build new services, roles, or partnerships." },

  // G: Influence/collaboration (10 items)
  { id: "G1", tag: "G", text: "I build relationships with external agencies such as ICBs, PCNs, universities and community partners proactively." },
  { id: "G2", tag: "G", text: "I am comfortable negotiating and advocating for the practice." },
  { id: "G3", tag: "G", text: "I tend to look for shared wins rather than a 'winner and loser'." },
  { id: "G4", tag: "G", text: "I can read a room and adjust my approach to keep progress moving." },
  { id: "G5", tag: "G", text: "I think good partnership decisions balance practice needs with system realities." },
  { id: "G6", tag: "G", text: "I keep allies informed and bring people with me on change." },
  { id: "G7", tag: "G", text: "I trust my partners to make decisions without having to be involved in all decisions." },
  { id: "G8", tag: "G", text: "I value transparent communication even when the message is difficult." },
  { id: "G9", tag: "G", text: "I prefer decisions that improve trust and alignment between partners." },
  { id: "G10", tag: "G", text: "I manage conflict by surfacing issues early rather than avoiding them." },

  // H: Personal resilience (10 items)
  { id: "H1", tag: "H", text: "I make decisions quickly when the consequences of delay are high." },
  { id: "H2", tag: "H", text: "I stay calm and constructive when others are stressed." },
  { id: "H3", tag: "H", text: "I can tolerate uncertainty without becoming paralysed." },
  { id: "H4", tag: "H", text: "I am comfortable being held accountable for outcomes." },
  { id: "H5", tag: "H", text: "I protect my own boundaries so I can sustain leadership over time." },
  { id: "H6", tag: "H", text: "I am willing to change my mind when presented with new evidence." },
  { id: "H7", tag: "H", text: "I address problems directly rather than hoping they resolve themselves." },
  { id: "H8", tag: "H", text: "I recover quickly after setbacks and keep moving." },
  { id: "H9", tag: "H", text: "I can say no without feeling guilty when it is the right decision." },
  { id: "H10", tag: "H", text: "I prefer clarity and action over prolonged debate." },

  // I: Sustainability strain (6 items)
  { id: "I1", tag: "I", text: "I often feel emotionally drained by partnership responsibilities." },
  { id: "I2", tag: "I", text: "I find it difficult to switch off from work outside working hours." },
  { id: "I3", tag: "I", text: "I feel my workload is sustainable over the next two years." },
  { id: "I4", tag: "I", text: "I feel energised by partnership responsibilities." },
  { id: "I5", tag: "I", text: "I have enough recovery time away from work." },
  { id: "I6", tag: "I", text: "I feel supported when workload becomes excessive." },

  // J: Work-life balance (5 items)
  { id: "J1", tag: "J", text: "Partnership responsibilities negatively impact my personal or family life." },
  { id: "J2", tag: "J", text: "I feel able to set boundaries without guilt." },
  { id: "J3", tag: "J", text: "My work role aligns with the life I want outside work." },
  { id: "J4", tag: "J", text: "I feel pressure to prioritise work over personal commitments." },
  { id: "J5", tag: "J", text: "I can take time off without concern for consequences." },

  // K: Conflict engagement (5 items)
  { id: "K1", tag: "K", text: "I address conflict early and directly." },
  { id: "K2", tag: "K", text: "I avoid conflict even when it is necessary." },
  { id: "K3", tag: "K", text: "I feel comfortable giving candid feedback to partners." },
  { id: "K4", tag: "K", text: "I worry about damaging relationships when raising concerns." },
  { id: "K5", tag: "K", text: "After disagreement, I actively work to rebuild trust and move forward." },

  // L: Leadership vs management (6 items)
  { id: "L1", tag: "L", text: "Leadership and management require different skill sets." },
  { id: "L2", tag: "L", text: "Management is primarily about processes and delivery, while leadership is about direction and culture." },
  { id: "L3", tag: "L", text: "I feel confident distinguishing leadership responsibilities from management responsibilities." },
  { id: "L4", tag: "L", text: "A partner can be an effective leader without personally managing day-to-day operations." },
  { id: "L5", tag: "L", text: "Partners should focus on strategic leadership more than day-to-day management of the practice." },
  { id: "L6", tag: "L", text: "In our practice, it is clear who is leading, who is managing, and how those roles work together." },

  // M: Purpose vs profit (6 items)
  { id: "M1", tag: "M", text: "Improving financial performance is a primary purpose of partnership." },
  { id: "M2", tag: "M", text: "Financial sustainability matters, but it should not override patient care and staff wellbeing." },
  { id: "M3", tag: "M", text: "Feeling happy at work is more important than chasing profits or targets." },
  { id: "M4", tag: "M", text: "I am comfortable making financially driven decisions when the business case is clear." },
  { id: "M5", tag: "M", text: "Patient outcomes and equity should outweigh profit considerations when there is a trade-off." },
  { id: "M6", tag: "M", text: "I believe partnership success should be judged by more than finance and targets." },

  // N: Tenure fairness (4 items)
  { id: "N1", tag: "N", text: "Historical contributions are as important as current ones when considering fairness and influence." },
  { id: "N2", tag: "N", text: "Current contributions are more important than past contributions." },
  { id: "N3", tag: "N", text: "Long-standing partners should have greater influence because of what they have built." },
  { id: "N4", tag: "N", text: "Influence should reflect present responsibility rather than tenure." },

  // O: Autonomy expectations (3 items)
  { id: "O1", tag: "O", text: "Autonomy should be expected as a partner." },
  { id: "O2", tag: "O", text: "The expectations and responsibilities of partners should differ from those of employees." },
  { id: "O3", tag: "O", text: "I prefer to focus on individual patient care rather than leadership or management responsibilities." },

  // P: Values/wellbeing/consensus (15 items)
  { id: "P1", tag: "P", text: "I am clear on the values that guide the practice." },
  { id: "P2", tag: "P", text: "I prefer to take a broader big-picture approach to finances." },
  { id: "P3", tag: "P", text: "I think private services should be part of an NHS practice." },
  { id: "P4", tag: "P", text: "Profit division should be based on who generated them." },
  { id: "P5", tag: "P", text: "It is important for partners to undertake external work for connections and reputation." },
  { id: "P6", tag: "P", text: "If I am happy then we don't need to change things." },
  { id: "P7", tag: "P", text: "I would like the practice to constantly expand and develop." },
  { id: "P8", tag: "P", text: "I am happy with my current financial position." },
  { id: "P9", tag: "P", text: "Practice work or stress keeps me awake at night." },
  { id: "P10", tag: "P", text: "I worry about the practice when I am not there." },
  { id: "P11", tag: "P", text: "I am able to accept a group decision I dislike and move on without revisiting this." },
  { id: "P12", tag: "P", text: "I get frustrated with a lack of consensus." },
  { id: "P13", tag: "P", text: "My partners care about me both professionally and personally." },
  { id: "P14", tag: "P", text: "I feel at risk of burning out." },
  { id: "P15", tag: "P", text: "The benefits of being a partner outweigh additional stress and work." },
];

// Create a map for quick lookup by ID
export const QUESTIONS_MAP = new Map<string, Question>(
  QUESTIONS.map(q => [q.id, q])
);

// Total question count
export const TOTAL_QUESTIONS = QUESTIONS.length; // 130

// Estimated time in minutes
export const ESTIMATED_TIME_MINUTES = 25; // 20-30 minutes
